import React from 'react';
import moment from 'moment-timezone';
import { formats, mapLookupsToSelectOptions } from '@shared/utils';
import { BooleanView, getTableColumn, Select, TableColumn, TextTooltip } from '@shared/UI';
import { IBmoTransaction } from '@shared/api';
import { useBmoTransactionsTable } from './BmoTransactionsTableProvider';
import { useLookups } from '@shared/providers/LookupsProvider';

export function getBmoTransactionsTableColumns(): TableColumn<IBmoTransaction>[] {
    const {
        transactionTypes,
        bmoReconciledStatus,
        bmoDebitCreditStatus,
        setTransactionTypes,
        setBmoDebitCreditStatus,
        setBmoReconciledStatus,
    } = useBmoTransactionsTable();

    const { lookups } = useLookups();
    const transactionTypeOptions =
        lookups && mapLookupsToSelectOptions('Etna.FundingApi.Client.TransactionType', lookups);
    const transactionTypesLookup = lookups?.find(
        (lookup) => lookup.name === 'Etna.FundingApi.Client.TransactionType',
    )?.items;
    const сdtDbtIndTypeOptions = lookups && mapLookupsToSelectOptions('CdtDbtInd', lookups);
    const сdtDbtIndLookup = lookups?.find((lookup) => lookup.name === 'CdtDbtInd')?.items;
    const bmoReconciledStatusesOptions = [
        { label: 'Reconciled', value: 'true' },
        { label: 'Not Reconciled', value: 'false' },
    ];

    return [
        getTableColumn({
            dataField: 'txId',
            title: 'ID',
            flex: 0.7,
            firstColumn: true,
            formatter: (data) => (
                <TextTooltip
                    text={String(data)}
                    maxLength={10}
                />
            ),
        }),
        getTableColumn({
            dataField: 'txDtTm',
            flex: 1,
            title: 'Date',
            formatter: (data) => moment(String(data)).format(formats.fullTime),
        }),
        getTableColumn({
            title: 'Amount',
            dataField: 'amt',
            flex: 0.7,
            formatter: (cell, row) => `${row.currency}${cell}`,
        }),
        getTableColumn({
            dataField: 'cdDbtInd',
            flex: 0.7,
            formatter: (cell) => {
                const type = сdtDbtIndLookup?.find((option) => option.id === cell);
                return type?.displayName || type?.name;
            },
            headerFormatter: () => (
                <Select
                    currentValue={bmoDebitCreditStatus}
                    options={сdtDbtIndTypeOptions || []}
                    dropdownPlacement='bottom-right'
                    placeholder='Debit/Credit'
                    nonInputView
                    withClearButton
                    onSelect={setBmoDebitCreditStatus}
                />
            ),
        }),
        getTableColumn({
            dataField: 'type',
            flex: 1,
            formatter: (cell) => {
                const type = transactionTypesLookup?.find((option) => option.id === cell);
                return type?.displayName || type?.name;
            },
            headerFormatter: () => (
                <Select
                    currentValue={transactionTypes}
                    options={transactionTypeOptions || []}
                    dropdownPlacement='bottom-right'
                    placeholder='Transaction Type'
                    nonInputView
                    withClearButton
                    onSelect={setTransactionTypes}
                />
            ),
        }),
        getTableColumn({
            dataField: 'sourceAccount',
            flex: 0.8,
        }),
        getTableColumn({
            dataField: 'targetAccount',
            flex: 0.8,
        }),
        getTableColumn({
            dataField: 'isReconciled',
            flex: 0.7,
            formatter: (data) => <BooleanView is={!!data} />,
            headerFormatter: () => (
                <Select
                    currentValue={bmoReconciledStatus}
                    options={bmoReconciledStatusesOptions}
                    dropdownPlacement='bottom-right'
                    placeholder='Is Reconciled'
                    nonInputView
                    withClearButton
                    onSelect={setBmoReconciledStatus}
                />
            ),
        }),
    ];
}
